import React, { useEffect, useState } from "react";
import {
  Accordion,
  Avatar,
  Badge,
  Box,
  Button,
  Center,
  Flex,
  Grid,
  Group,
  Image,
  Loader,
  Paper,
  Stack,
  Text,
  Title,
  UnstyledButton,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { AppResources, AppRoutes } from "@/constants";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "@/utils/stores/authStore";
import { getSubscriptionInfo } from "@/utils/billing/api";
import type { SubscriptionInfo } from "@/utils/billing/types";
import {
  IconShieldCheck,
  IconChevronDown,
  IconLogout,
  IconCheck,
  IconMail,
} from "@tabler/icons-react";
import { showIntercom } from "@/utils/intercom";

interface ExpiredSubscriptionProps {
  email?: string;
}

/**
 * Subscription status type for determining the UI state.
 * - "expired": User had an active subscription that ended
 * - "canceled": User canceled their subscription
 * - "never_subscribed": User created an account but never subscribed
 * - "loading": Still fetching subscription info
 */
type SubscriptionStatus =
  "expired" | "canceled" | "never_subscribed" | "loading";

/**
 * Format a date string to a human-readable format.
 */
const formatDate = (dateString: string | null): string => {
  if (!dateString) return "";
  const date = new Date(dateString);
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(date);
};

/**
 * Progress steps shown in the left sidebar (only for owners).
 */
const progressSteps = [
  { id: "status", title: "Account Status", completed: true },
  { id: "plan", title: "Choose Your Plan", completed: false },
  { id: "payment", title: "Complete Payment", completed: false },
  { id: "access", title: "Regain Access", completed: false },
];

/**
 * WizzyAvatar - Animated Wizzy character with hover effect
 */
const WizzyAvatar = ({ size = 260 }: { size?: number }) => {
  const [isHovered, setIsHovered] = useState(false);

  const wizzyStyle = (active?: boolean) =>
    ({
      position: "absolute",
      inset: 0,
      transition: "opacity 220ms ease-in-out",
      willChange: "opacity",
      opacity: isHovered ? (active ? 1 : 0) : active ? 0 : 1,
    }) as const;

  return (
    <Box
      style={{
        position: "relative",
        width: size,
        height: size,
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Avatar
        src={AppResources.wizzyId}
        alt="Wizzy"
        size={size}
        radius="xl"
        style={wizzyStyle()}
      />
      <Avatar
        src={AppResources.wizzyWave}
        alt="Wizzy waving"
        size={size}
        radius="xl"
        style={wizzyStyle(true)}
      />
    </Box>
  );
};

/**
 * Props for the reusable ActionCard component.
 */
interface ActionCardProps {
  /** Title displayed on the card */
  title: string;
  /** Description text displayed below the title */
  description: string;
  /** Image source URL (mutually exclusive with icon) */
  imageSrc?: string;
  /** Image alt text */
  imageAlt?: string;
  /** Icon component to display (mutually exclusive with imageSrc) */
  icon?: React.ReactNode;
  /** Background color for icon container */
  iconBgColor?: string;
  /** Click handler - if not provided, card is not interactive */
  onClick?: () => void;
  /** Href for link-based cards */
  href?: string;
}

/**
 * ActionCard - A reusable card component for displaying actions.
 *
 * Supports either an image or icon visual, with a title and description.
 * Can be interactive (clickable/linkable) or static based on props.
 *
 * @param props - ActionCard configuration options
 */
const ActionCard: React.FC<ActionCardProps> = ({
  title,
  description,
  imageSrc,
  imageAlt,
  icon,
  iconBgColor,
  onClick,
  href,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const isInteractive = Boolean(onClick || href);

  /**
   * Renders the visual element - either an image or an icon in a styled container.
   */
  const renderVisual = () => {
    if (imageSrc) {
      return (
        <Image
          src={imageSrc}
          alt={imageAlt || title}
          h={164}
          fit="contain"
          radius="xl"
        />
      );
    }

    if (icon) {
      return (
        <Box
          style={{
            width: 164,
            height: 164,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: iconBgColor || "var(--mantine-color-blue-0)",
            borderRadius: "var(--mantine-radius-xl)",
          }}
        >
          {icon}
        </Box>
      );
    }

    return null;
  };

  /**
   * Card paper styles with hover effect for interactive cards.
   */
  const paperStyles = {
    minHeight: 260,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderColor:
      isHovered && isInteractive
        ? "var(--mantine-color-blue-5)"
        : "var(--mantine-color-gray-3)",
    boxShadow:
      isHovered && isInteractive
        ? "0 4px 20px rgba(0, 119, 204, 0.15)"
        : undefined,
    transform: isHovered && isInteractive ? "translateY(-2px)" : undefined,
    transition: "all 0.2s ease",
  };

  /**
   * Renders the card content (visual + title + description).
   */
  const cardContent = (
    <Paper
      shadow="xs"
      radius="md"
      p="xl"
      withBorder
      h="100%"
      style={paperStyles}
    >
      <Stack align="center" gap="md" h="100%" justify="space-between">
        {renderVisual()}
        <Stack align="center" gap="xs" style={{ flexGrow: 1 }}>
          <Title order={3} fw={600} ta="center">
            {title}
          </Title>
          <Text c="dimmed" ta="center" size="sm">
            {description}
          </Text>
        </Stack>
      </Stack>
    </Paper>
  );

  // Interactive card with onClick handler
  if (onClick) {
    return (
      <UnstyledButton
        onClick={onClick}
        h="100%"
        w="100%"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {cardContent}
      </UnstyledButton>
    );
  }

  // Interactive card with href (link)
  if (href) {
    return (
      <UnstyledButton
        component="a"
        href={href}
        h="100%"
        w="100%"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {cardContent}
      </UnstyledButton>
    );
  }

  // Static (non-interactive) card
  return cardContent;
};

/**
 * ExpiredSubscription - A trust-building page to help users reactivate their subscription.
 *
 * This component displays the onboarding-style layout with:
 * - Left sidebar with Wizzy mascot and progress steps
 * - Right content area with status info and action cards
 * - Dynamic messaging based on subscription status
 */
/**
 * Props for individual step items in the progress legend.
 */
interface StepItemProps {
  step: { id: string; title: string; completed: boolean };
  index: number;
}

/**
 * StepItem - A single step in the progress legend with status-based styling.
 *
 * @param props - Step configuration and index
 */
const StepItem: React.FC<StepItemProps> = ({ step, index }) => {
  const isCompleted = step.completed;
  const isCurrent = index === 0;

  /**
   * Get the indicator styles based on step status.
   */
  const getIndicatorStyles = () => {
    const baseStyles = {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: 32,
      height: 32,
      borderRadius: "50%",
      flexShrink: 0,
      transition: "all 0.2s ease",
    };

    if (isCompleted) {
      return {
        ...baseStyles,
        backgroundColor: "var(--mantine-color-green-6)",
        color: "white",
      };
    }
    if (isCurrent) {
      return {
        ...baseStyles,
        backgroundColor: "var(--mantine-color-blue-6)",
        color: "white",
      };
    }
    return {
      ...baseStyles,
      backgroundColor: "var(--mantine-color-gray-2)",
      color: "var(--mantine-color-gray-6)",
    };
  };

  /**
   * Get the container styles based on step status.
   */
  const getContainerStyles = () => {
    const baseStyles = {
      display: "flex",
      alignItems: "center",
      gap: "1rem",
      padding: "1rem 1.25rem",
      borderRadius: 10,
      transition: "all 0.2s ease",
      minHeight: "3rem",
    };

    if (isCurrent) {
      return {
        ...baseStyles,
        backgroundColor: "var(--mantine-color-blue-0)",
        border: "2px solid var(--mantine-color-blue-6)",
      };
    }
    return baseStyles;
  };

  /**
   * Get the title color based on step status.
   */
  const getTitleColor = () => {
    if (isCompleted) return "green.7";
    if (isCurrent) return "blue.9";
    return "gray.6";
  };

  return (
    <Box style={getContainerStyles()}>
      <Box style={getIndicatorStyles()}>
        {isCompleted ? (
          <IconCheck size={20} />
        ) : (
          <Text size="sm" fw={600}>
            {index + 1}
          </Text>
        )}
      </Box>
      <Text
        size="1rem"
        fw={isCurrent ? 600 : 500}
        c={getTitleColor()}
        style={{ lineHeight: 1.4, transition: "all 0.2s ease" }}
      >
        {step.title}
      </Text>
    </Box>
  );
};

const ExpiredSubscription: React.FC<ExpiredSubscriptionProps> = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading, refreshAuth, role } = useAuthStore();
  const hideWizzy = useMediaQuery("(max-width: 1100px)");
  const [subscriptionInfo, setSubscriptionInfo] =
    useState<SubscriptionInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<SubscriptionStatus>("loading");
  const [taxProEmail, setTaxProEmail] = useState<string | null>(null);

  /**
   * Determine if user is the account owner (can reactivate subscription).
   */
  const isOwner = role === "owner";

  /**
   * Determine if user is a team member or team lead.
   */
  const isTeamMemberOrLead = role === "team_member" || role === "team_lead";

  /**
   * Determine if user is a client.
   */
  const isClient = role === "client";

  /**
   * Refresh auth state and fetch subscription info on mount.
   */
  useEffect(() => {
    refreshAuth();
  }, [refreshAuth]);

  /**
   * Fetch the tax professional's email for clients.
   * This is used to enable the "Contact Your Tax Professional" mailto link.
   */
  useEffect(() => {
    if (isClient) {
      // Try to get tax pro email from localStorage (if available from previous sessions)
      const personalInfo = localStorage.getItem("personalInfo");
      if (personalInfo) {
        try {
          const info = JSON.parse(personalInfo);
          const email = info?.assignee?.email ?? info?.businessOwnerEmail;
          if (email) {
            setTaxProEmail(email);
          }
        } catch (e) {
          console.error("Error parsing personalInfo:", e);
        }
      }
    }
  }, [isClient]);

  /**
   * Fetch subscription information to determine the status reason.
   */
  useEffect(() => {
    const fetchSubscriptionStatus = async () => {
      try {
        setLoading(true);
        const info = await getSubscriptionInfo();
        setSubscriptionInfo(info);

        // Determine status based on subscription info and user data
        if (info.planEndDate && !info.isActive) {
          setStatus("expired");
        } else if (user?.hadSubscription && !info.isActive) {
          setStatus("canceled");
        } else if (!user?.hadSubscription && !info.isActive) {
          setStatus("never_subscribed");
        } else {
          setStatus("expired");
        }
      } catch (error) {
        console.error("Error fetching subscription info:", error);
        if (user?.hadSubscription) {
          setStatus("expired");
        } else {
          setStatus("never_subscribed");
        }
      } finally {
        setLoading(false);
      }
    };

    if (!authLoading) {
      fetchSubscriptionStatus();
    }
  }, [authLoading, user?.hadSubscription]);

  /**
   * Get the user's first name for personalized greeting.
   */
  const firstName = user?.firstName || "";

  /**
   * Get the status badge configuration.
   */
  const getStatusBadge = (): { label: string; color: string } => {
    switch (status) {
      case "expired":
        return { label: "Access Paused", color: "orange.7" };
      case "canceled":
        return { label: "Subscription Canceled", color: "red" };
      case "never_subscribed":
        return { label: "Setup Incomplete", color: "blue" };
      default:
        return { label: "Access Paused", color: "orange" };
    }
  };

  /**
   * Get the reason text based on subscription status and user role.
   * @returns Descriptive text explaining the current subscription state
   */
  const getReasonText = (): string => {
    // Role-specific messaging for team members/leads
    if (isTeamMemberOrLead) {
      switch (status) {
        case "expired":
          return subscriptionInfo?.planEndDate
            ? `Your team's subscription ended on ${formatDate(subscriptionInfo.planEndDate)}.`
            : "Your team's subscription has ended.";
        case "canceled":
          return "Your team's subscription was canceled.";
        default:
          return "Your team's subscription is not active.";
      }
    }

    // Role-specific messaging for clients
    if (isClient) {
      return "Your tax professional's subscription is not currently active.";
    }

    // Default messaging for owners
    switch (status) {
      case "expired":
        return "Your subscription has ended or is currently inactive.";
      case "canceled":
        return "Your subscription was canceled.";
      case "never_subscribed":
        return "You created an account but haven't chosen a plan yet.";
      default:
        return "Your subscription is not active.";
    }
  };

  /**
   * Get the header greeting text based on user role.
   * @returns Personalized greeting for the user
   */
  const getHeaderText = (): string => {
    if (isTeamMemberOrLead) {
      return firstName ? `Hi ${firstName}` : "Hi there";
    }
    if (isClient) {
      return firstName ? `Hi ${firstName}` : "Hi there";
    }
    return firstName ? `Welcome Back, ${firstName}` : "Welcome Back";
  };

  /**
   * Get the subheader text based on user role.
   * @returns Instructions for next steps
   */
  const getSubheaderText = (): string => {
    if (isTeamMemberOrLead) {
      return `${reasonText} Please reach out to your account owner to resolve this.`;
    }
    if (isClient) {
      return `${reasonText} Please reach out to your tax professional for assistance.`;
    }
    return `${reasonText} Let's get you back on track.`;
  };

  /**
   * Handle primary CTA click - navigate to plan selection.
   */
  const handleReactivate = () => {
    navigate(AppRoutes.register);
  };

  /**
   * Handle logout click.
   */
  const handleLogout = () => {
    navigate(AppRoutes.logout);
  };

  /**
   * Get FAQ items for the accordion based on user role.
   * @returns Array of FAQ items tailored to the user's role
   */
  const getFaqItems = () => {
    // FAQ items for team members and team leads
    if (isTeamMemberOrLead) {
      return [
        {
          value: "expired",
          question: "Why can't I access my account?",
          answer:
            "Your account owner's subscription has ended or been canceled. Once they reactivate the subscription, you'll automatically regain access.",
        },
        {
          value: "contact",
          question: "How do I contact the account owner?",
          answer:
            "Reach out to your account owner directly to let them know about the subscription status. They can reactivate the subscription to restore access for the entire team.",
        },
        {
          value: "data",
          question: "Is my work saved?",
          answer:
            "Yes, all your data and work is safely preserved. Once the subscription is reactivated, everything will be exactly as you left it.",
        },
      ];
    }

    // FAQ items for clients
    if (isClient) {
      return [
        {
          value: "expired",
          question: "Why can't I access my account?",
          answer:
            "Your tax professional's subscription has ended or been canceled. Please reach out to them to resolve this issue.",
        },
        {
          value: "contact",
          question: "How do I contact my tax professional?",
          answer:
            "Use the contact information your tax professional provided when you signed up, or use the email link on this page to reach out to them.",
        },
        {
          value: "data",
          question: "Is my information safe?",
          answer:
            "Yes, all your tax information and documents are safely stored and protected. Once access is restored, everything will be available again.",
        },
      ];
    }

    // Default FAQ items for owners
    return [
      {
        value: "expired",
        question: "My subscription expired",
        answer:
          "Your subscription period has ended. You can reactivate anytime to regain full access to SmartWiz CRM and all features.",
      },
      {
        value: "payment",
        question: "A payment failed",
        answer:
          "If a payment didn't go through, your subscription may have been paused. Reactivating will prompt you to update your payment method.",
      },
      {
        value: "canceled",
        question: "I canceled my subscription",
        answer:
          "If you previously canceled, you can come back anytime. Your data has been preserved and you'll pick up right where you left off.",
      },
    ];
  };

  const faqItems = getFaqItems();

  const statusBadge = getStatusBadge();
  const reasonText = getReasonText();
  const isPastCustomer = status === "expired" || status === "canceled";

  // Show loading state
  if (authLoading || loading) {
    return (
      <Center w="100%" h="100vh">
        <Stack align="center" justify="center" h="100vh" gap="lg">
          <Loader size="lg" color="blue" />
          <Text c="dimmed">Loading your account...</Text>
        </Stack>
      </Center>
    );
  }

  return (
    <Flex
      direction={{ base: "column-reverse", md: "row" }}
      w="100%"
      h={hideWizzy ? "auto" : "100vh"}
      px="md"
    >
      {/* Left Column - Wizzy + Progress */}
      {!hideWizzy && (
        <Stack align="center" justify="center" h="100%" w="100%" maw={500}>
          {/* Wizzy Avatar - hidden on smaller screens */}

          <Box mb="2rem">
            <WizzyAvatar size={260} />
          </Box>

          {/* Progress Legend - only shown for owners */}
          {isOwner && (
            <Box p="1rem" w="100%" maw={300}>
              <Text fw={600} size="1rem" mb="md" c="dimmed">
                Your Progress
              </Text>
              <Stack gap="sm">
                {progressSteps.map((step, index) => (
                  <StepItem key={step.id} step={step} index={index} />
                ))}
              </Stack>
            </Box>
          )}
        </Stack>
      )}

      {/* Right Column - Main Content */}
      <Stack
        gap="xl"
        maw={900}
        w="100%"
        mx="auto"
        justify="center"
        p="xl"
        mt="2rem"
      >
        {/* Header */}
        <Stack gap="xs">
          <Title order={1} fw={700} size="2rem">
            {getHeaderText()}
          </Title>
          <Text size="lg" c="dimmed">
            {getSubheaderText()}
          </Text>
        </Stack>

        {/* Status Badge */}
        <Group justify="space-between" w="100%">
          <Badge
            size="lg"
            radius="lg"
            color={statusBadge.color}
            variant="light"
            w="fit-content"
            p="md"
          >
            <Text size="md" fw={600} c={statusBadge.color}>
              {statusBadge.label}
            </Text>
          </Badge>
          {/* Trust & Reassurance Box */}
          <Badge p="md" radius="lg" color="green" variant="light">
            <Group gap="sm" align="center" justify="center">
              <IconShieldCheck
                size={24}
                color="var(--mantine-color-green-6)"
                style={{ flexShrink: 0, marginTop: 2 }}
              />
              <Stack gap={4}>
                <Text fw={600} size="sm">
                  {isClient
                    ? "Your information is protected"
                    : "Your client data is protected"}
                </Text>
              </Stack>
            </Group>
          </Badge>
        </Group>

        {/* Action Cards */}
        <Grid gutter="lg">
          {/* Primary Action Card - varies by role */}
          {isOwner && (
            <Grid.Col span={{ base: 12, md: 6 }}>
              <ActionCard
                title={
                  isPastCustomer ? "Reactivate Subscription" : "Explore Plans"
                }
                description={
                  isPastCustomer
                    ? "Get back to where you left off with full access"
                    : "I'm ready to purchase SmartWiz for my business"
                }
                imageSrc={AppResources.wizzyRocket}
                imageAlt="Wizzy with rocket"
                onClick={handleReactivate}
              />
            </Grid.Col>
          )}

          {isTeamMemberOrLead && (
            <Grid.Col span={{ base: 12, md: 6 }}>
              <ActionCard
                title="Contact Account Owner"
                description="Reach out to your account owner to let them know about the subscription status"
                icon={
                  <IconMail
                    size={80}
                    color="var(--mantine-color-blue-6)"
                    stroke={1.5}
                  />
                }
                iconBgColor="var(--mantine-color-blue-0)"
              />
            </Grid.Col>
          )}

          {isClient && (
            <Grid.Col span={{ base: 12, md: 6 }}>
              <ActionCard
                title="Contact Your Tax Professional"
                description={
                  taxProEmail
                    ? `Click to email ${taxProEmail}`
                    : "Reach out to your tax professional to let them know you're having trouble accessing your account"
                }
                icon={
                  <IconMail
                    size={80}
                    color="var(--mantine-color-teal-6)"
                    stroke={1.5}
                  />
                }
                iconBgColor="var(--mantine-color-teal-0)"
                href={
                  taxProEmail
                    ? `mailto:${taxProEmail}?subject=Account Access Issue`
                    : undefined
                }
              />
            </Grid.Col>
          )}

          {/* Talk to Support Card */}
          <Grid.Col span={{ base: 12, md: 6 }}>
            <ActionCard
              title={isClient ? "Need Help?" : "Talk to Support"}
              description={
                isClient
                  ? "Our support team can help you connect with your tax professional"
                  : "Have questions? Our team is here to help you"
              }
              imageSrc={AppResources.wizzyOnboard}
              imageAlt="Wizzy support"
              onClick={() => showIntercom()}
            />
          </Grid.Col>
        </Grid>

        {/* Why am I seeing this? Accordion */}
        <Accordion
          variant="contained"
          radius="md"
          chevron={<IconChevronDown size={16} />}
          styles={{
            item: {
              border: "1px solid var(--mantine-color-gray-3)",
              backgroundColor: "var(--mantine-color-gray-0)",
            },
            control: {
              padding: "12px 16px",
            },
            label: {
              fontWeight: 500,
              fontSize: 14,
            },
            content: {
              padding: "0 16px 16px",
            },
          }}
        >
          <Accordion.Item value="why">
            <Accordion.Control>
              <Text size="sm" fw={500}>
                Why am I seeing this?
              </Text>
            </Accordion.Control>
            <Accordion.Panel>
              <Stack gap="md">
                {faqItems.map((item) => (
                  <Stack key={item.value} gap={4}>
                    <Text size="sm" fw={600} c="dark.6">
                      {item.question}
                    </Text>
                    <Text size="sm" c="dimmed" style={{ lineHeight: 1.5 }}>
                      {item.answer}
                    </Text>
                  </Stack>
                ))}
              </Stack>
            </Accordion.Panel>
          </Accordion.Item>
        </Accordion>

        {/* Sign in link */}
        <Group justify="space-between" w="100%">
          <Text size="sm" c="dimmed" ta="center">
            Need help?{" "}
            <Text
              span
              c="blue"
              fw={500}
              onClick={() => showIntercom()}
              style={{ textDecoration: "none", cursor: "pointer" }}
            >
              Contact us
            </Text>
          </Text>
          <Button
            variant="subtle"
            color="gray"
            size="sm"
            leftSection={<IconLogout size={16} />}
            onClick={handleLogout}
          >
            Sign out
          </Button>
        </Group>
      </Stack>
    </Flex>
  );
};

export default ExpiredSubscription;
